import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Loader2, BadgeCheck, Users, FileImage, Instagram, Twitter, ArrowLeft, Sparkles, Heart, Check, Megaphone, Trophy, MessagesSquare, ShieldCheck } from "lucide-react";
import api, { mediaUrl, formatApiErrorDetail, formatCount } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PostCard from "@/components/PostCard";
import CheckoutModal from "@/components/CheckoutModal";
import { SupporterBadge } from "@/lib/badges";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function CreatorPublic() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [following, setFollowing] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);
  const [subscribed, setSubscribed] = useState(false);
  const [checkout, setCheckout] = useState(false);
  const [sponsors, setSponsors] = useState([]);
  const [supporters, setSupporters] = useState([]);

  useEffect(() => {
    api.get("/sponsorships").then(({ data }) => setSponsors(data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get(`/creators/${id}/supporters`).then(({ data }) => setSupporters(data)).catch(() => setSupporters([]));
  }, [id]);

  const fetchCreator = async () => {
    const { data } = await api.get(`/creators/${id}`);
    setData(data);
    setFollowing(data.profile.is_following);
    setFollowerCount(data.profile.follower_count);
    setSubscribed(data.profile.is_subscribed);
  };

  const startConversation = async () => {
    try {
      const { data } = await api.post("/conversations", { creator_id: id });
      navigate(`/messaggi?c=${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Impossibile aprire la chat");
    }
  };

  useEffect(() => {
    (async () => {
      try {
        await fetchCreator();
      } catch (e) {
        setData(false);
      }
    })();
  }, [id]);

  const toggleFollow = async () => {
    if (!user) { navigate("/login"); return; }
    try {
      const { data } = await api.post(`/creators/${id}/follow`);
      setFollowing(data.is_following);
      setFollowerCount(data.follower_count);
      toast.success(data.is_following ? "Ora segui questo creator" : "Non segui più");
    } catch (e) {
      toast.error("Errore");
    }
  };

  const openSubscribe = () => {
    if (!user) { navigate("/login"); return; }
    setCheckout(true);
  };

  const confirmSubscribe = async () => {
    try {
      await api.post("/subscriptions", { creator_id: id });
      toast.success("Abbonamento attivo!", { description: "Ora hai accesso a tutti i contenuti di questo creator." });
      await fetchCreator();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore nel pagamento");
      throw e;
    }
  };

  if (data === null) {
    return <div className="min-h-screen flex items-center justify-center bg-[#0D0D0D]"><Loader2 className="w-8 h-8 text-[#E91E8C] animate-spin" /></div>;
  }
  if (data === false) {
    return <div className="min-h-screen flex items-center justify-center bg-[#0D0D0D] text-white/60">Creator non trovato.</div>;
  }

  const { profile, posts } = data;
  const theme = profile.theme_color || "#E91E8C";

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white overflow-x-hidden">
      {/* Cover — cinematic full-bleed */}
      <div className="relative h-64 sm:h-80 lg:h-[420px]">
        <img src={mediaUrl(profile.cover_url)} alt="cover" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D] via-[#0D0D0D]/40 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-2/3 pointer-events-none" style={{ background: `radial-gradient(70% 90% at 50% 130%, ${theme}55, transparent 70%)` }} />
        <button
          onClick={() => navigate(user ? "/feed" : "/")}
          data-testid="back-btn"
          className="absolute top-5 left-5 glass w-10 h-10 rounded-full flex items-center justify-center border border-white/10 hover:border-white/30 transition-colors active:scale-95"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
      </div>

      <div className="max-w-3xl mx-auto px-5 sm:px-8 -mt-24 sm:-mt-28 relative">
        <div className="flex items-end justify-between mb-5 flex-wrap gap-4">
          <div className="relative shrink-0">
            <img
              src={mediaUrl(profile.avatar_url)} alt={profile.display_name}
              className="w-32 h-32 sm:w-36 sm:h-36 rounded-full object-cover ring-4 ring-[#0D0D0D]"
              style={{ boxShadow: `0 0 40px ${theme}66, 0 0 0 2px ${theme}` }}
            />
            {profile.is_verified && (
              <span className="absolute bottom-1.5 right-1.5 w-8 h-8 rounded-full bg-[#0D0D0D] flex items-center justify-center ring-2 ring-[#0D0D0D]">
                <BadgeCheck className="w-6 h-6 text-[#F5C6D6]" />
              </span>
            )}
          </div>
          <div className="flex gap-3">
            <Button
              onClick={toggleFollow} variant="outline" data-testid="follow-btn"
              className="rounded-full border-white/20 bg-transparent hover:bg-white/5 gap-2"
            >
              {following ? <><Check className="w-4 h-4" /> Segui già</> : <><Heart className="w-4 h-4" /> Segui</>}
            </Button>
            {user?.role === "fan" && (
              <Button
                onClick={startConversation} variant="outline" data-testid="message-creator-btn"
                className="rounded-full border-white/20 bg-transparent hover:bg-white/5 gap-2"
              >
                <MessagesSquare className="w-4 h-4" /> Messaggio
              </Button>
            )}
            {profile.profile_type === "subscription" ? (
              subscribed ? (
                <Button
                  disabled data-testid="subscribed-badge"
                  className="rounded-full font-semibold text-white bg-white/10 gap-2 disabled:opacity-100"
                >
                  <Check className="w-4 h-4" /> Abbonato
                </Button>
              ) : (
                <Button
                  onClick={openSubscribe} data-testid="subscribe-btn"
                  className="rounded-full font-semibold text-white hover:opacity-90"
                  style={{ backgroundColor: theme }}
                >
                  Abbonati · €{Number(profile.subscription_price).toFixed(2)}/mese
                </Button>
              )
            ) : (
              <span
                data-testid="free-profile-badge"
                className="inline-flex items-center gap-1.5 rounded-full border border-[#F5C6D6]/40 text-[#F5C6D6] px-4 py-2 text-sm font-medium"
              >
                <Sparkles className="w-4 h-4" /> Profilo gratuito
              </span>
            )}
          </div>
        </div>

        <h1 className="text-3xl sm:text-4xl font-black font-heading mb-1 tracking-tight">{profile.display_name}</h1>
        {profile.twofa_protected && (
          <div className="inline-flex items-center gap-1.5 mb-3 text-xs font-semibold text-green-400 bg-green-400/10 border border-green-400/20 rounded-full px-3 py-1" data-testid="account-protected-badge">
            <ShieldCheck className="w-3.5 h-3.5" /> Account protetto
          </div>
        )}
        <p className="text-white/70 mb-4 leading-relaxed">{profile.bio}</p>

        <div className="flex items-center gap-6 mb-4 text-sm">
          <span className="flex items-center gap-1.5 text-white/70" data-testid="follower-count"><Users className="w-4 h-4" style={{ color: theme }} /> {formatCount(followerCount)} follower</span>
          <span className="flex items-center gap-1.5 text-white/70"><FileImage className="w-4 h-4" style={{ color: theme }} /> {profile.post_count} post</span>
        </div>

        {(profile.social_links?.instagram || profile.social_links?.twitter) && (
          <div className="flex items-center gap-3 mb-8">
            {profile.social_links.instagram && (
              <span className="flex items-center gap-1.5 text-sm text-white/60"><Instagram className="w-4 h-4" /> {profile.social_links.instagram}</span>
            )}
            {profile.social_links.twitter && (
              <span className="flex items-center gap-1.5 text-sm text-white/60"><Twitter className="w-4 h-4" /> {profile.social_links.twitter}</span>
            )}
          </div>
        )}

        {sponsors.filter((s) => s.creator_id !== profile.id).length > 0 && (
          <div className="mb-6" data-testid="public-sponsored-section">
            <div className="flex items-center gap-2 mb-3">
              <Megaphone className="w-4 h-4 text-[#F5C6D6]" />
              <h2 className="text-xs font-bold font-heading uppercase tracking-wider text-[#F5C6D6]">Sponsorizzato</h2>
            </div>
            <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1">
              {sponsors.filter((s) => s.creator_id !== profile.id).map((s) => (
                <Link
                  key={s.creator_id} to={`/creator/${s.creator_id}`} data-testid={`public-sponsored-${s.creator_id}`}
                  className="shrink-0 w-36 bg-[#1A1A1A] border border-[#F5C6D6]/30 rounded-2xl p-3 card-hover-glow"
                >
                  <div className="relative w-14 h-14 mx-auto mb-2">
                    <img src={mediaUrl(s.avatar_url)} alt={s.display_name} className="w-14 h-14 rounded-full object-cover border border-white/10" />
                    <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-[8px] font-bold uppercase tracking-wider bg-[#F5C6D6] text-black px-1.5 py-0.5 rounded-full">ADV</span>
                  </div>
                  <div className="flex items-center justify-center gap-1">
                    <span className="text-xs font-semibold truncate">{s.display_name}</span>
                    {s.is_verified && <BadgeCheck className="w-3 h-3 text-[#F5C6D6] shrink-0" />}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {supporters.length > 0 && (
          <div className="mb-6" data-testid="top-supporters-section">
            <div className="flex items-center gap-2 mb-3">
              <Trophy className="w-4 h-4" style={{ color: theme }} />
              <h2 className="text-xs font-bold font-heading uppercase tracking-wider" style={{ color: theme }}>Top Supporter</h2>
            </div>
            <div className="bg-[#1A1A1A] border border-white/10 rounded-2xl divide-y divide-white/5">
              {supporters.map((s, i) => (
                <div key={s.fan_id} data-testid={`supporter-${s.fan_id}`} className="flex items-center gap-3 px-4 py-3">
                  <span className="w-5 text-center text-sm font-black font-heading text-white/40">{i + 1}</span>
                  <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 font-bold text-sm" style={{ backgroundColor: `${theme}22`, color: theme }}>
                    {(s.display_name || "?")[0].toUpperCase()}
                  </div>
                  <span className="flex-1 min-w-0 text-sm font-semibold truncate">{s.display_name}</span>
                  <SupporterBadge badge={s.badge} />
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="border-t border-white/10 pt-8 pb-16">
          <h2 className="text-lg font-bold font-heading mb-5 flex items-center gap-2">
            <span className="inline-block w-1.5 h-5 rounded-full" style={{ backgroundColor: theme }} /> Contenuti
          </h2>
          {posts.length === 0 ? (
            <p className="text-white/50 text-center py-10">Nessun contenuto ancora.</p>
          ) : (
            <div className="space-y-6" data-testid="creator-posts">
              {posts.map((p) => <PostCard key={p.id} post={p} onUnlocked={fetchCreator} />)}
            </div>
          )}
        </div>
      </div>

      <CheckoutModal
        open={checkout}
        onOpenChange={setCheckout}
        title="Abbonati a questo creator"
        subtitle={`Abbonamento mensile a ${profile.display_name}`}
        lines={[
          { label: "Creator", value: profile.display_name },
          { label: "Piano", value: "Abbonamento mensile" },
          { label: "Rinnovo", value: "Ogni 30 giorni" },
        ]}
        total={profile.subscription_price}
        confirmLabel="Simula pagamento riuscito"
        onConfirm={confirmSubscribe}
      />
    </div>
  );
}
